/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage } from 'react-intl';
import { Navigate, useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Message from 'carbon-react/lib/components/message';

import { useModal } from '@futrli/shared-services';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { PageWidthWrapper } from '@futrli/ui-components/components/page-width-wrapper/page-width-wrapper';
import { SettingsSubNav } from '@futrli/predict/settings/components/settings-sub-nav/settings-sub-nav';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { useNavHeight } from '@futrli/predict/hooks/useNavHeight';
import { getWorkspaceListingUrl } from '../../../utils/route-utils';
import { UsersView } from '@futrli/views/views/users/UsersView/UsersView';
import { WorkspaceListView } from '@futrli/views/views/workspaces/WorkspaceListView/WorkspaceListView';

import { useUsersFilters } from '../../hooks/use-users-filters/use-users-filters';
import { UsersActionsProvider } from '../../context/users-actions-context/users-actions-context';
import { UsersList } from '../../components/users-list/users-list';
import { UsersListFilterBar } from '../../components/users-list-filter-bar/users-list-filter-bar';
import { InviteUserSidebar } from '../../sidebars/invite-user-sidebar/invite-user-sidebar';

export const UsersListContainer = () => {
  const flags = useFlags();
  const addUser = useModal();
  const { navHeight } = useNavHeight();
  const { workspaceId = '' } = useParams();

  const usersView = useApiView(useMemo(() => new UsersView(workspaceId), [workspaceId]));
  const workspaceView = useApiView(useMemo(() => new WorkspaceListView(flags, workspaceId), [flags, workspaceId]));
  const { errorLoading, isLoading, roles, users } = usersView.state;
  const { hasMembersPermission, workspace, workspaces } = workspaceView.state;
  const {
    filteredUsers,
    onRoleFilterChange,
    onSearchChange,
    onStatusFilterChange,
    roleFilterValue,
    searchTerm,
    statusFilterValue
  } = useUsersFilters(users, workspaceId);

  const isWorkspaceLoaded = !!workspaces?.some(w => w.id === workspaceId);
  if (isWorkspaceLoaded && !hasMembersPermission) {
    return <Navigate to={getWorkspaceListingUrl(workspaceId)} replace />;
  }

  return (
    <>
      <SettingsSubNav
        hasUsersPermission={hasMembersPermission}
        workspaceId={workspaceId}
        onlyExternalOrgs={workspace?.flags.isPureSFA || false}
        workspacesCount={workspaces?.length || 0}
      />

      <PageWidthWrapper>
        <UsersActionsProvider workspaceId={workspaceId}>
          <Box margin="auto" padding="32px 0">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <ListingPageHeader
                title={<FormattedMessage id="common.user_overview" defaultMessage="User overview" />}
              />

              {hasMembersPermission && (
                <Button buttonType="primary" onClick={addUser.open}>
                  <FormattedMessage id="common.add_user" defaultMessage="Add user" />
                </Button>
              )}
            </Box>
            {errorLoading ? (
              <Box p={4} display="flex" justifyContent="center">
                <Message variant="error" open>
                  <FormattedMessage
                    id="predict.error_loading_data"
                    defaultMessage="An error occurred while loading the data from the server."
                  />
                </Message>
              </Box>
            ) : (
              <>
                <UsersListFilterBar
                  onRoleFilterChange={onRoleFilterChange}
                  onSearchChange={onSearchChange}
                  onStatusFilterChange={onStatusFilterChange}
                  roleFilterValue={roleFilterValue}
                  roles={roles}
                  searchTerm={searchTerm}
                  statusFilterValue={statusFilterValue}
                  stickyTop={navHeight}
                />
                <Box mt={3}>
                  <UsersList isLoading={isLoading} users={filteredUsers} />
                </Box>
              </>
            )}
          </Box>
          {hasMembersPermission && (
            <InviteUserSidebar isOpen={addUser.isOpen} onClose={addUser.close} workspaceId={workspaceId} />
          )}
        </UsersActionsProvider>
      </PageWidthWrapper>
    </>
  );
};
